import { z } from 'zod/v4';
import type { Bookmark, Collection, Tag } from './bookmark';
import type { User } from './user';

// Common API response wrapper
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  data: z.unknown().optional(),
  error: z.string().optional(),
});

export type ApiResponse<T = unknown> = {
  success: boolean;
  data?: T;
  error?: string;
};

// Pagination
export const PaginationParamsSchema = z.object({
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(100).default(20),
});

export type PaginationParams = z.infer<typeof PaginationParamsSchema>;

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    pagination: z.object({
      page: z.number(),
      limit: z.number(),
      total: z.number(),
      totalPages: z.number(),
    }),
  });

export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
};

// Error response
export const ErrorResponseSchema = z.object({
  success: z.literal(false),
  error: z.string(),
  code: z.string().optional(),
});

export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

// Bookmark list response with extended data
export interface BookmarkWithRelations extends Bookmark {
  tags?: Tag[];
  collections?: Collection[];
}

// User with tier info
export interface UserWithTierInfo extends User {
  bookmarkCount: number;
  collectionCount: number;
  tierLimits: {
    maxBookmarks: number | null; // null = unlimited
    maxCollections: number | null;
  };
}
